import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from "./Home.module.css";
import duas from "../data/duas.json";
import AuthPromptModal from "../components/AuthPromptModal";

function Home() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [accessMessage, setAccessMessage] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [duaOfTheDay, setDuaOfTheDay] = useState(null);
    const [showDua, setShowDua] = useState(false);
    const [showAuthPrompt, setShowAuthPrompt] = useState(false);
    const [authPromptMessage, setAuthPromptMessage] = useState("");
    const [showPlusNotice, setShowPlusNotice] = useState(false);
    const plusTimerRef = useRef(null);

    useEffect(() => {
        if (accessMessage) {
            const timeout = setTimeout(() => {
                setAccessMessage("");
            }, 4000);
            return () => clearTimeout(timeout);
        }
    }, [accessMessage]);

    useEffect(() => {
        try {
            const key = "duaOfTheDay";
            const today = new Date().toISOString().split("T")[0];
            const cachedRaw = localStorage.getItem(key);
            if (cachedRaw) {
                try {
                    const cached = JSON.parse(cachedRaw);
                    if (cached && cached.date === today && typeof cached.index === "number" && duas && duas[cached.index]) {
                        setDuaOfTheDay(duas[cached.index]);
                        return;
                    }
                } catch (err) {
                    // ignore parse errors
                }
            }

            const msPerDay = 24 * 60 * 60 * 1000;
            const dayNumber = Math.floor(Date.now() / msPerDay);
            const idx = duas && duas.length ? dayNumber % duas.length : 0;
            try {
                localStorage.setItem(key, JSON.stringify({ date: today, index: idx }));
            } catch (err) {
                // ignore storage errors
            }
            setDuaOfTheDay(duas[idx]);
        } catch (e) {
            console.error("Failed to select dua of the day", e);
            setDuaOfTheDay(duas[0] || null);
        }
    }, []);

    const handleProtectedClick = (path) => {
        const hasToken = user && user.token;
        if (!isAuthenticated || !hasToken) {
            setAuthPromptMessage("🚫 Please login or sign up to access this feature.");
            setShowAuthPrompt(true);
            return;
        }

        setAccessMessage("");
        navigate(path);
    };

    const closeAuthPrompt = () => setShowAuthPrompt(false);

    const handleTryNowClick = () => {
        setShowPlusNotice(true);
        if (plusTimerRef.current) clearTimeout(plusTimerRef.current);
        plusTimerRef.current = setTimeout(() => {
            setShowPlusNotice(false);
            plusTimerRef.current = null;
        }, 2500);
    };

    // cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (plusTimerRef.current) {
                clearTimeout(plusTimerRef.current);
                plusTimerRef.current = null;
            }
        };
    }, []);

    const cards = [
        { icon: "📋", label: "Quiz", path: "/quiz", protected: false },
        { icon: "✏️", label: "Fill in blanks", path: "/ayah-blanks", protected: false },
        { icon: "⚖️", label: "Halal vs Haram", path: "/halalvsharam", protected: false },
        { icon: "🃏", label: "Flashcards", path: "/flashcards", protected: false },
        { icon: "🎯", label: "Figure match", path: "/figurematchgame", protected: false },
    ];

    return (
        <>
            <div className={styles.mainContainer}>
                {/* Sidebar */}
                <div className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ""}`}>
                    <div className={styles.sidebarContent}>
                        <h1 className={styles.sidebarTitle}>DeenRoute</h1>
                        <nav className={styles.sidebarNav}>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    handleProtectedClick("/journey");
                                }}
                                className={styles.navItem}
                            >
                                Start Your Journey
                            </button>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate("/quiz");
                                }}
                                className={styles.navItem}
                            >
                                Quiz
                            </button>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate("/halalvsharam");
                                }}
                                className={styles.navItem}
                            >
                                Halal vs haram
                            </button>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate("/ayah-blanks");
                                }}
                                className={styles.navItem}
                            >
                                Fillinblanks
                            </button>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate("/flashcards");
                                }}
                                className={styles.navItem}
                            >
                                Flashcards
                            </button>
                            <button
                                onClick={() => {
                                    setSidebarOpen(false);
                                    navigate("/figure-match-game");
                                }}
                                className={styles.navItem}
                            >
                                Figure-match-game
                            </button>
                            <button
                                onClick={() => {
                                    navigate("/leaderboard");
                                    setSidebarOpen(false);
                                }}
                                className={styles.navItem}
                            >
                                Leaderboard
                            </button>
                            <button
                                onClick={() => {
                                    navigate("/login");
                                    setSidebarOpen(false);
                                }}
                                className={styles.navItem}
                            >
                                Login
                            </button>
                            <button
                                onClick={() => {
                                    navigate("/signup");
                                    setSidebarOpen(false);
                                }}
                                className={styles.navItem}
                            >
                                Signup
                            </button>
                        </nav>

                        {/* Try DeenRoute Plus Widget - Mobile */}
                        <div className={styles.deenutePlusWidgetMobile}>
                            <div className={styles.deenroutePlusHeader}>
                                <span className={styles.deenroutePlusBadge}>PLUS</span>
                            </div>
                            <p className={styles.deenroutePlusTitle}>Try DeenRoute Plus on the app</p>
                            <p className={styles.deenroutePlusDescription}>Unlimited lessons and personalized learning!</p>
                            <button className={styles.deenroutePlusBtn} onClick={handleTryNowClick}>
                                TRY NOW
                            </button>
                        </div>
                    </div>
                </div>

                {/* Overlay */}
                {sidebarOpen && (
                    <div
                        className={styles.sidebarOverlay}
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <div className={styles.content}>
                    {/* Header */}
                    <div className={styles.header}>
                        <button
                            className={styles.menuBtn}
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            ☰
                        </button>
                        <h2 className={styles.homeTitle}>Home</h2>
                        <div className={styles.headerIcons}>
                            <button
                                className={styles.iconBtn}
                                onClick={() => navigate("/settings")}
                            >
                                ⚙️
                            </button>
                            <button
                                className={styles.iconBtn}
                                onClick={() => navigate("/profile")}
                            >
                                👤
                            </button>
                        </div>
                    </div>

                    {/* Main Content Wrapper */}
                    <div className={styles.mainContentWrapper}>
                        {/* Left Section */}
                        <div className={styles.leftSection}>
                            {/* Greeting Section */}
                            <div className={styles.greetingSection}>
                                <h3 className={styles.greeting}>Assalāmu 'alaykum</h3>
                                <p className={styles.greetingSubtext}>May Allah make your learning easy today.</p>

                                {/* Dua of the Day Card */}
                                <div className={styles.duaCard}>
                                    <div className={styles.duaHeader}>
                                        <span>☀️</span>
                                        <span className={styles.duaTitle}>Dua of the day</span>
                                        <span>⤴️</span>
                                    </div>
                                    {duaOfTheDay ? (
                                        <>
                                            <p className={styles.duaText}>
                                                {duaOfTheDay.translation.length > 150
                                                    ? `${duaOfTheDay.translation.slice(0, 150)}...`
                                                    : duaOfTheDay.translation}
                                            </p>
                                            {showDua && (
                                                <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
                                                    <p style={{ fontSize: "1.1rem", direction: "rtl" }}>{duaOfTheDay.arabic}</p>
                                                    <p style={{ fontStyle: "italic", color: "#444" }}>{duaOfTheDay.transliteration}</p>
                                                    <p style={{ marginTop: "0.5rem" }}>{duaOfTheDay.translation}</p>
                                                    <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                                                        {duaOfTheDay.reference} — {duaOfTheDay.source}
                                                    </p>
                                                </div>
                                            )}
                                            <button className={styles.viewBtn} onClick={() => setShowDua(!showDua)}>
                                                {showDua ? "Hide" : "View"}
                                            </button>
                                        </>
                                    ) : (
                                        <p className={styles.duaText}>Loading dua...</p>
                                    )}
                                </div>

                                {/* Progress Bar */}
                                <div className={styles.progressContainer}>
                                    <div className={styles.progressBar}>
                                        <div className={styles.progressFill} style={{ width: "0%" }}></div>
                                    </div>
                                    <div className={styles.progressText}>
                                        <span>0/5 min</span>
                                        <span className={styles.flame}>🔥 2</span>
                                    </div>
                                </div>
                            </div>

                            {/* Cards Grid */}
                            <div className={styles.cardsGrid}>
                                {cards.map((card, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() =>
                                            card.protected
                                                ? handleProtectedClick(card.path)
                                                : navigate(card.path)
                                        }
                                        className={styles.card}
                                    >
                                        <span className={styles.cardIcon}>{card.icon}</span>
                                        <span className={styles.cardLabel}>{card.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Right Section - Widgets (Desktop Only) */}
                        <div className={styles.rightSection}>
                            {/* Try DeenRoute Plus Widget */}
                            <div className={styles.deenroutePlusWidget}>
                                <div className={styles.deenroutePlusHeader}>
                                    <span className={styles.deenroutePlusBadge}>PLUS</span>
                                </div>
                                <p className={styles.deenroutePlusTitle}>Try DeenRoute Plus on the app</p>
                                <p className={styles.deenroutePlusDescription}>Unlimited lessons and personalized learning!</p>
                                <button className={styles.deenroutePlusBtn} onClick={handleTryNowClick}>
                                    TRY NOW
                                </button>
                            </div>

                            {/* Leaderboard Widget */}
                            <div className={styles.leaderboardWidget}>
                                <h3 className={styles.leaderboardWidgetTitle}>🏆 Leaderboard</h3>
                                <p className={styles.leaderboardWidgetText}>See how you rank among learners</p>
                                <button
                                    onClick={() => navigate("/leaderboard")}
                                    className={styles.leaderboardWidgetBtn}
                                >
                                    GO TO LEADERBOARD
                                </button>
                            </div>
                        </div>
                    </div>

                    {accessMessage && (
                        <div className={styles.accessMessage}>{accessMessage}</div>
                    )}
                    <AuthPromptModal open={showAuthPrompt} onClose={closeAuthPrompt} message={authPromptMessage} />
                </div>
            </div>

            {/* Plus Notice - Portal-like (rendered OUTSIDE main container to escape stacking context) */}
            {showPlusNotice && (
                <div
                    className={styles.plusNoticeOverlay}
                    onClick={() => {
                        setShowPlusNotice(false);
                        if (plusTimerRef.current) {
                            clearTimeout(plusTimerRef.current);
                            plusTimerRef.current = null;
                        }
                    }}
                >
                    <div className={styles.plusNoticeBox}>Mobile app in progress — enjoy the web app for now!</div>
                </div>
            )}
        </>
    );
}

export default Home;